import { useMutation } from '@apollo/client';
import {
  ANNOTATE_QUESTION_SCALE,
  GET_CHARTABLE_FIELDS,
} from '@employ-engage/common-components';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChartableField } from './chart-spec-builder';

export interface QuestionScoringPanelProps {
  templateId: string;
  field: ChartableField;
  onClose: () => void;
}

const inputClass =
  'w-16 rounded border-gray-300 px-2 py-1 text-sm dark:bg-dark-secondaryBackground';

/**
 * Lets an author say what a question's options mean: which are ordered, and
 * which say "this did not apply to me".
 *
 * Both are read back against the version a response was filled on, so this
 * reinterprets answers already collected rather than only affecting future
 * ones — which is the whole point, since the alternative is re-surveying.
 */
export function QuestionScoringPanel({
  templateId,
  field,
  onClose,
}: QuestionScoringPanelProps) {
  const { t } = useTranslation(['common']);
  const [annotate, { loading }] = useMutation(ANNOTATE_QUESTION_SCALE, {
    refetchQueries: [
      { query: GET_CHARTABLE_FIELDS, variables: { templateId } },
    ],
    awaitRefetchQueries: true,
  });

  const [scores, setScores] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      field.options.map((option) => [
        option.label,
        option.score === null || option.score === undefined
          ? ''
          : String(option.score),
      ])
    )
  );
  const [notApplicable, setNotApplicable] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        field.options.map((option) => [option.label, !!option.notApplicable])
      )
  );
  const [error, setError] = useState<string | null>(null);

  const suggestion = field.suggestedScale;

  const applySuggestion = () => {
    if (!suggestion) return;
    setScores(
      Object.fromEntries(
        suggestion.optionScores.map((entry) => [
          entry.label,
          entry.score === null || entry.score === undefined
            ? ''
            : String(entry.score),
        ])
      )
    );
  };

  const save = async () => {
    setError(null);
    // Option ids live on the version, not on the catalogue entry, so the
    // suggestion is what carries them. Without one we can only match labels,
    // which the server refuses — so scoring is offered where ids are known.
    const byLabel = new Map(
      (suggestion?.optionScores ?? []).map((entry) => [
        entry.label,
        entry.optionId,
      ])
    );

    const options = field.options
      .map((option) => {
        const optionId = byLabel.get(option.label);
        if (!optionId) return null;
        const raw = scores[option.label];
        return {
          optionId,
          score: raw === '' ? null : Number(raw),
          notApplicable: !!notApplicable[option.label],
        };
      })
      .filter(Boolean);

    if (!options.length) {
      setError(t('This question’s options could not be matched.'));
      return;
    }

    const numeric = options
      .map((o) => (o as { score: number | null }).score)
      .filter((s): s is number => typeof s === 'number');

    try {
      const { data } = await annotate({
        variables: {
          templateId,
          questionId: (field.ref as { questionId?: string }).questionId,
          scale: numeric.length
            ? {
                min: Math.min(...numeric),
                max: Math.max(...numeric),
                direction: 'higherIsBetter',
                midpointPolicy: 'score',
                preset: suggestion?.presetId ?? 'custom',
              }
            : null,
          options,
        },
      });
      const result = data?.annotateQuestionScale;
      if (
        result &&
        result.versionsAnnotated === 0 &&
        result.versionsSkipped > 0
      ) {
        setError(
          t('Nothing changed — these options were already scored this way.')
        );
        return;
      }
      onClose();
    } catch (e) {
      setError(t('That could not be saved.'));
    }
  };

  return (
    <div className="mt-4 rounded-md border border-light-alternate p-4 dark:border-dark-alternate">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">
            {t('What do these answers mean?')}
          </h4>
          <p className="mt-1 text-xs text-light-secondaryText dark:text-dark-secondaryText">
            {t(
              'Scoring ordered answers lets this question be averaged. Marking an answer “did not apply” takes those people out of the percentage instead of counting them as a no.'
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-xs text-light-secondaryText hover:underline"
        >
          {t('Cancel')}
        </button>
      </div>

      {suggestion && (
        <button
          type="button"
          onClick={applySuggestion}
          className="mt-3 rounded border border-light-primary px-3 py-1.5 text-xs font-medium text-light-primary"
        >
          {t('These look like a {{name}} — fill in the scores', {
            name: suggestion.name,
          })}
        </button>
      )}

      <table className="mt-3 w-full">
        <thead>
          <tr className="text-left text-xs text-light-secondaryText">
            <th className="py-1 font-medium">{t('Answer')}</th>
            <th className="w-24 py-1 font-medium">{t('Score')}</th>
            <th className="w-32 py-1 font-medium">{t('Did not apply')}</th>
          </tr>
        </thead>
        <tbody>
          {field.options.map((option) => (
            <tr
              key={option.label}
              className="border-t border-light-alternate/50"
            >
              <td className="py-1.5 pr-3 text-sm">{option.label}</td>
              <td className="py-1.5">
                <input
                  type="number"
                  className={inputClass}
                  value={scores[option.label] ?? ''}
                  // A not-applicable answer has no position on the scale, so
                  // the two controls are deliberately exclusive.
                  disabled={!!notApplicable[option.label]}
                  onChange={(e) =>
                    setScores((prev) => ({
                      ...prev,
                      [option.label]: e.target.value,
                    }))
                  }
                  aria-label={t('Score for {{label}}', { label: option.label })}
                />
              </td>
              <td className="py-1.5">
                <input
                  type="checkbox"
                  checked={!!notApplicable[option.label]}
                  onChange={(e) =>
                    setNotApplicable((prev) => ({
                      ...prev,
                      [option.label]: e.target.checked,
                    }))
                  }
                  aria-label={t('{{label}} means the question did not apply', {
                    label: option.label,
                  })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {field.hasInconsistentScale && (
        <p className="mt-3 rounded bg-semantic-warning/20 p-2 text-xs">
          {t(
            'Versions of this survey score these answers differently, so scores are ignored until they agree.'
          )}
        </p>
      )}

      {error && <p className="mt-3 text-xs text-semantic-error">{error}</p>}

      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={loading}
          className={classNames(
            'rounded-md bg-light-primary px-3 py-1.5 text-sm font-medium text-white',
            loading && 'opacity-60'
          )}
        >
          {loading ? t('Saving…') : t('Save')}
        </button>
        <span className="text-xs text-light-secondaryText">
          {t('Applies to answers already collected.')}
        </span>
      </div>
    </div>
  );
}

export default QuestionScoringPanel;
